<template>
  <div class="tracking-route-container">
    <div
      v-if="loading"
      style="display: flex; justify-content: center; align-items: center; padding: 2rem 0"
    >
      <a-spin size="large" />
    </div>

    <div
      v-else-if="!expressSn || expressCode === '-1' || expressCode === '-2'"
      style="text-align: center; color: #999; padding: 2rem 0"
    >
      <p>{{ $t('该物流方式暂不支持查询轨迹') }}</p>
    </div>

    <div
      v-else-if="!routeData || !sortedRoutes.length"
      style="text-align: center; color: #999; padding: 2rem 0"
    >
      <p>{{ $t('暂无物流轨迹信息') }}</p>
      <a-button
        type="link"
        size="small"
        :loading="syncLoading"
        style="margin-top: 1rem"
        @click="handleSync"
      >
        <a-icon type="sync" :spin="syncLoading" />
        {{ $t('同步轨迹') }}
      </a-button>
    </div>

    <div v-else class="tracking-route-list">
      <div
        style="
          margin-bottom: 1rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
        "
      >
        <div style="font-size: 14px; color: #666">
          <span style="font-weight: 500">{{ $t('快递公司') }}：</span>
          {{ expressCode }}
          <span style="margin-left: 1rem; font-weight: 500">{{ $t('快递单号') }}：</span>
          {{ expressSn }}
        </div>
        <a-button type="link" size="small" :loading="syncLoading" @click="handleSync">
          <a-icon type="sync" :spin="syncLoading" />
          {{ $t('同步轨迹') }}
        </a-button>
      </div>

      <div class="route-timeline">
        <div
          v-for="(route, index) in sortedRoutes"
          :key="index"
          class="route-item"
          :class="{ 'route-item-last': index === sortedRoutes.length - 1 }"
        >
          <div class="route-dot-wrapper">
            <div class="route-dot" :class="{ 'route-dot-active': index === 0 }" />
            <div v-if="index !== sortedRoutes.length - 1" class="route-line" />
          </div>
          <div class="route-content">
            <div class="route-header">
              <span :style="index === 0 ? 'color: #1890ff; font-weight: 600;' : 'color: #666;'">
                {{ route.orgRouteAction || route.org_route_action }}
              </span>
              <span class="route-time">{{ formatTime(route.routeTime || route.route_time) }}</span>
            </div>
            <div class="route-message">
              {{ route.message }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/service/api'
import http from '@/service/axios'

export default {
  name: 'TrackingRoute',
  props: {
    expressSn: {
      type: String,
    },
    expressCode: {
      type: String,
    },
    shopCode: {
      type: String,
      default: '',
    },
    orderId: {
      type: String,
      default: '',
    },
    autoLoad: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      loading: false,
      routeData: null,
      syncLoading: false,
    }
  },
  computed: {
    sortedRoutes() {
      if (!this.routeData || !Array.isArray(this.routeData.routes)) {
        return []
      }
      return [...this.routeData.routes].sort((a, b) => {
        const timeA = new Date(a.routeTime || a.route_time || 0).getTime()
        const timeB = new Date(b.routeTime || b.route_time || 0).getTime()
        return timeB - timeA // 倒序，最新的在前
      })
    },
  },
  mounted() {
    if (this.autoLoad) {
      this.loadData().catch(() => {})
    }
  },
  watch: {
    expressSn() {
      if (this.autoLoad) {
        this.loadData().catch(() => {})
      }
    },
    expressCode() {
      if (this.autoLoad) {
        this.loadData().catch(() => {})
      }
    },
  },
  methods: {
    formatTime(timestamp) {
      if (!timestamp) return ''
      // 如果已经是格式化好的字符串且包含 '-', 且不是数字，则直接返回
      if (typeof timestamp === 'string' && timestamp.includes('-') && isNaN(Number(timestamp))) {
        return timestamp
      }
      const date = new Date(timestamp)
      if (isNaN(date.getTime())) return timestamp
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const seconds = String(date.getSeconds()).padStart(2, '0')
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
    },
    async loadData(force_new = false) {
      if (!this.orderId) {
        return
      }
      if (!this.orderId && (this.expressCode === '-1' || this.expressCode === '-2')) {
        this.routeData = null
        return
      }
      try {
        this.loading = true
        const params = {
          merchantCode: localStorage.getItem('merchantCode'),
          supplierReturnOrderId: this.orderId,
          force_new,
        }

        let res = await http({
          url: api.queryTrackingRoute,
          type: 'post',
          data: params,
        })
        this.routeData = res.result || res
      } catch (error) {
        console.error('查询物流轨迹失败:', error)
        if (!force_new) {
          this.$message.error(this.$t('查询物流轨迹失败，请重试'))
        }
        this.routeData = null
        throw error
      } finally {
        this.loading = false
      }
    },
    async handleSync() {
      if (!this.expressSn) {
        return
      }
      this.syncLoading = true
      try {
        await this.loadData(true)
        this.$message.success(this.$t('同步物流轨迹成功'))
        this.$emit('refresh')
      } catch (error) {
        console.error('同步物流轨迹失败:', error)
      } finally {
        this.syncLoading = false
      }
    },
  },
}
</script>

<style scoped>
.tracking-route-container {
  min-height: 200px;
}

.tracking-route-list {
  max-height: 600px;
  overflow-y: auto;
}

.route-timeline {
  position: relative;
  padding-left: 24px;
}

.route-item {
  position: relative;
  padding-bottom: 24px;
}

.route-item-last {
  padding-bottom: 0;
}

.route-dot-wrapper {
  position: absolute;
  left: -24px;
  top: 6px;
}

.route-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: #d9d9d9;
  border: 2px solid #fff;
  position: relative;
  z-index: 1;
}

.route-dot-active {
  background-color: #1890ff;
  width: 14px;
  height: 14px;
}

.route-line {
  position: absolute;
  left: 5px;
  top: 14px;
  width: 2px;
  height: calc(100% + 10px);
  background-color: #e8e8e8;
}

.route-content {
  flex: 1;
}

.route-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.route-time {
  font-size: 12px;
  color: #999;
}

.route-message {
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  word-break: break-word;
}
</style>
